import { useLocale } from '@/components/Link'
import { localeDate } from '@/components/page-specific/catering/common/helpers/date'
import { useDeliveryTimeLabel } from '@/components/page-specific/catering/common/helpers/useDeliveryTimeLabel'
import type { UserBasketSettingsLocationFragment } from '@/generated/graphql'
import useTenant from '@/hooks/useTenant'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import EditIcon from '@mui/icons-material/Edit'
import GroupIcon from '@mui/icons-material/Group'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import ScheduleIcon from '@mui/icons-material/Schedule'
import { Box, IconButton, Stack, Typography, alpha } from '@mui/material'

import type React from 'react'
import { useTranslation } from 'react-i18next'

import { useAddressLabel } from '../input-bar/useAddressLabel'

export interface ReorderBasketInputBarProps {
  orderSelectedSettings: {
    date?: number
    time?: string
    headCount?: number
    location?: UserBasketSettingsLocationFragment
  }
  disabled?: boolean
  hideDate?: boolean
  dateHasError?: boolean
  onEditClick: () => void
}

const Field: React.FC<{
  icon: React.ReactNode
  value: React.ReactNode
  emptyValue: string
  isEmpty: boolean
  onEdit?: () => void
  disabled?: boolean
  fullWidth?: boolean
  hasError?: boolean
}> = ({ icon, value, emptyValue, isEmpty, onEdit, disabled, fullWidth, hasError }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.125}
    component="button"
    type="button"
    disabled={disabled || !onEdit}
    onClick={onEdit}
    sx={{
      border: '1px solid',
      borderColor: hasError ? 'error.main' : 'divider',
      borderRadius: '8px',
      padding: '9px 11px',
      fontSize: 13,
      bgcolor: hasError ? (theme) => alpha(theme.palette.error.main, 0.06) : 'grey.50',
      flex: fullWidth ? '1 1 100%' : 1,
      minWidth: 0,
      fontFamily: 'inherit',
      textAlign: 'left',
      cursor: disabled || !onEdit ? 'default' : 'pointer',
      transition: 'border-color .15s, background .15s',
      boxShadow: hasError ? (theme) => `0 0 0 1px ${theme.palette.error.main}` : 'none',
      '&:hover:not(:disabled)': {
        borderColor: hasError ? 'error.main' : 'primary.main',
      },
      '&:focus-visible': {
        outline: 'none',
        borderColor: hasError ? 'error.main' : 'primary.main',
        bgcolor: 'background.paper',
      },
    }}>
    <Box
      sx={{
        display: 'flex',
        color: hasError ? 'error.main' : 'primary.main',
        opacity: hasError ? 1 : 0.8,
        '& svg': { fontSize: 15 },
      }}>
      {icon}
    </Box>
    <Typography
      sx={{
        fontWeight: 500,
        flex: 1,
        fontSize: 13,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        color: hasError ? 'error.main' : isEmpty ? 'text.disabled' : 'text.primary',
      }}>
      {isEmpty ? emptyValue : value}
    </Typography>
    {onEdit && !disabled && (
      <Box
        component="span"
        sx={{
          padding: 0.25,
          color: hasError ? 'error.main' : 'primary.main',
          opacity: hasError ? 1 : 0.6,
          display: 'inline-flex',
        }}>
        <EditIcon sx={{ fontSize: 13 }} />
      </Box>
    )}
  </Stack>
)

export const ReorderBasketInputBar: React.FC<ReorderBasketInputBarProps> = ({
  orderSelectedSettings,
  disabled = false,
  hideDate = false,
  dateHasError = false,
  onEditClick,
}) => {
  const { t } = useTranslation()
  const locale = useLocale()
  const tenant = useTenant()
  const { addressLabel } = useAddressLabel(orderSelectedSettings.location)
  const timeLabel = useDeliveryTimeLabel(orderSelectedSettings.time) ?? ''

  // Tenants using gm delivery timeslots show longer range labels (e.g. "12:30 - 13:00"),
  // so give the time field more room and shrink the headcount field accordingly.
  const useTimeslots = Boolean(tenant.features?.gmDeliveryTimeslots)

  const dateLabel = orderSelectedSettings.date
    ? localeDate(orderSelectedSettings.date, 'do MMM yyyy', locale)
    : ''

  const headcountLabel =
    orderSelectedSettings.headCount != null ? String(orderSelectedSettings.headCount) : ''

  const handleEdit = disabled ? undefined : onEditClick

  return (
    <Stack
      spacing={0.75}
      sx={{ padding: '12px 12px 10px', borderBottom: '1px solid', borderColor: 'divider' }}>
      <Field
        icon={<LocationOnIcon />}
        value={addressLabel}
        emptyValue={t('marketplace:enterAnAddress')}
        isEmpty={!addressLabel}
        onEdit={handleEdit}
        disabled={disabled}
        fullWidth
      />
      {!hideDate && (
        <Field
          icon={<CalendarTodayIcon />}
          value={dateLabel}
          emptyValue={t('marketplace:deliveryDate')}
          isEmpty={!dateLabel}
          onEdit={handleEdit}
          disabled={disabled}
          fullWidth
          hasError={dateHasError}
        />
      )}
      <Stack direction="row" spacing={0.75}>
        <Box sx={{ flex: useTimeslots ? 3 : 1, minWidth: 0, display: 'flex' }}>
          <Field
            icon={<ScheduleIcon />}
            value={timeLabel}
            emptyValue={t('marketplace:time')}
            isEmpty={!timeLabel}
            onEdit={handleEdit}
            disabled={disabled}
            fullWidth
          />
        </Box>
        <Box sx={{ flex: useTimeslots ? 2 : 1, minWidth: 0, display: 'flex' }}>
          <Field
            icon={<GroupIcon />}
            value={headcountLabel}
            emptyValue={t('marketplace:headcount')}
            isEmpty={!headcountLabel}
            onEdit={handleEdit}
            disabled={disabled}
            fullWidth
          />
        </Box>
      </Stack>
    </Stack>
  )
}
